import { getInfo } from '@studio/models/gamepad';
import { useMount } from '@studio/react-utils/hooks';
import { MessageIds } from './constants.js';
import { useAnalytics } from '#~/analytics/hooks/index.js';
import { EVENT_CATEGORIES, EVENT_NAMES } from '#~/analytics/constants/index.js';
import { logger } from '#~/logger/index.js';
import { post } from '#~/notifications/index.js';

/** @internal */
const toMessageId = (id: Gamepad['id']): string => [MessageIds.gamepad, id].join('.');

/** `<Gamepads>` listener. */
export const Gamepads = (): null => {
  const { track } = useAnalytics();

  useMount(() => {
    const abortController = new AbortController();
    const { signal } = abortController;

    // Watch the windows's online status.
    window.addEventListener(
      'gamepadconnected',
      ({ gamepad }) => {
        const { name } = getInfo(gamepad);

        logger.info(`Gamepad connected: ${name}`);

        // Track the gamepad controller addition
        track(EVENT_NAMES.ADD_GAMEPAD_CONTROLLER, EVENT_CATEGORIES.CONTROLLERS, {
          controllerType: 'gamepad',
          controllerName: name,
          controllerId: gamepad.id
        });

        post({
          id: toMessageId(gamepad.id),
          message: `Your ${name} gamepad has been connected.`,
          severity: 'success',
          title: 'Gamepad Connected'
        });
      },
      { signal }
    );

    window.addEventListener(
      'gamepaddisconnected',
      ({ gamepad }) => {
        const { name } = getInfo(gamepad);

        logger.info(`Gamepad disconnected: ${name}`);

        // A disconnected gamepad is a status notice with no in-app action
        // (the user reconnects the hardware), so it must not light the bell.
        post({
          id: toMessageId(gamepad.id),
          message: `Your ${name} gamepad has been disconnected.`,
          title: 'Gamepad Disconnected'
        });
      },
      { signal }
    );

    return () => {
      abortController.abort();
    };
  });

  return null;
};
